/**
 * Where /app/login sends someone after they sign in (`?next=`). Only paths on
 * this site are honored, so the login page can't be used to send people to
 * another site; anything else falls back to the STASH Board.
 */

export const LOGIN_PATH = '/app/login';
export const DEFAULT_AFTER_LOGIN = '/app/stashboard';

const PLACEHOLDER_ORIGIN = 'https://stash.invalid';

export function safeNextPath(raw: string | null | undefined): string {
  if (!raw || !raw.startsWith('/') || raw.startsWith('//') || raw.startsWith('/\\')) return DEFAULT_AFTER_LOGIN;
  // Control characters and spaces are never part of a path we produce.
  if ([...raw].some((c) => c.charCodeAt(0) <= 0x20 || c.charCodeAt(0) === 0x7f)) return DEFAULT_AFTER_LOGIN;
  let url: URL;
  try {
    url = new URL(raw, PLACEHOLDER_ORIGIN);
  } catch {
    return DEFAULT_AFTER_LOGIN;
  }
  if (url.origin !== PLACEHOLDER_ORIGIN) return DEFAULT_AFTER_LOGIN;
  // Returning to the login page itself would loop.
  if (url.pathname === LOGIN_PATH) return DEFAULT_AFTER_LOGIN;
  return `${url.pathname}${url.search}${url.hash}`;
}

/** The login URL that returns to `next` afterwards (when `next` would be honored). */
export function loginPath(next?: string): string {
  if (!next) return LOGIN_PATH;
  const safe = safeNextPath(next);
  if (safe === DEFAULT_AFTER_LOGIN && next !== DEFAULT_AFTER_LOGIN) return LOGIN_PATH;
  return `${LOGIN_PATH}?next=${encodeURIComponent(safe)}`;
}

/** For full-page redirects to sign-in: return to the page the browser is on. */
export function currentPageLoginPath(): string {
  const { pathname, search, hash } = window.location;
  // Already on the login page (a late auth failure): keep its `next`.
  if (pathname === LOGIN_PATH) return `${pathname}${search}`;
  return loginPath(`${pathname}${search}${hash}`);
}
